import Space from 'antd/lib/space';
import Title from 'antd/lib/typography/Title';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import Search from 'antd/lib/input/Search';
import { SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Button from 'antd/lib/button';
import { AppPlatform, AppStoreMinimalApp } from '../../types';
import { Col, Row } from 'antd/lib/grid';
import SelectAppComponent from './SelectAppComponent';
import { uniq, remove, debounce, flatten } from 'lodash';
import Menu from 'antd/lib/menu';
import Dropdown from 'antd/lib/dropdown';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Checkbox from 'antd/lib/checkbox';
import { iconForPlatform } from '../../helpers/platformComponentHelpers';
import planeIllustrationUrl from '../../../assets/plane-illustration.svg';
import Radio from 'antd/lib/radio';

import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import * as API from '../../api';
import { selectAuthCredentials, selectOrg, selectOrgHasAnyApps } from '../../selectors/selectors';
import {
    AddAppData,
    AddAppsRequest,
    CreateAppRequest,
    tryAddApps,
    tryCreateApp,
    TRY_ADD_APPS,
    TRY_CREATE_APP,
} from '../../actions/app';
import { platformDifferentiatedAppId } from '../../helpers';
import allSortedCountries from '../../utils/countries';
import Select from 'antd/lib/select';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { filterOptionLabel } from '../../helpers/formHelpers';
import { globalMessage } from '../../utils/globalMessage';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface AddAppsComponentProps {
    orgId: string;
    open: boolean;
    showAsOnboarding: boolean;
    onCancel: () => void;
    onShowDemoMode: () => void;
}

interface SelectAppData extends AppStoreMinimalApp {
    isSelected: boolean;
}

export const AddAppsComponent: React.FC<AddAppsComponentProps> = (props: AddAppsComponentProps) => {
    const orgIdParam = useSelector((state: RootState) => selectOrg(state, props.orgId).orgId);
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));
    const orgHasApps = useSelector((state: RootState) => selectOrgHasAnyApps(state));

    const [selectedAppIds, setSelectedAppIds] = React.useState([]);
    const [appStoreData, setAppStoreData] = React.useState<AppStoreMinimalApp[]>([]); // raw data from the endpoint
    const [appData, setAppData] = React.useState<SelectAppData[]>([]); // data merged with selected ids, for table
    const [selectedCountryCode, setSelectedCountryCode] = React.useState('US');
    const [selectedCountryName, setSelectedCountryName] = React.useState('United States');
    const [searchIsLoading, setSearchIsLoading] = React.useState(false);
    const [addAppsIncludeOTA, setAddAppsIncludeOTA] = React.useState(false);

    const [showManualAddApp, setShowManualAddApp] = React.useState(false);
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const createAppIsLoading = useIsLoading(TRY_CREATE_APP);
    const createAppError = useError(TRY_CREATE_APP);
    const createAppSuccess = useUpdated(TRY_CREATE_APP);

    const addAppsIsLoading = useIsLoading(TRY_ADD_APPS);
    const addAppsError = useError(TRY_ADD_APPS);
    const addAppsSuccess = useUpdated(TRY_ADD_APPS);

    const addButtonDisabled = showManualAddApp ? false : selectedAppIds.length === 0;

    // for fetch apps request
    let controller: AbortController = new AbortController();

    const updateAppData = (appData: AppStoreMinimalApp[]) => {
        const selectAppData = appData.map((appStoreApp) => {
            const platformedAppId = platformDifferentiatedAppId(appStoreApp.appId, appStoreApp.appPlatform);
            const isSelected = selectedAppIds.includes(platformedAppId);

            return {
                ...appStoreApp,
                isSelected,
            };
        });

        setAppData(selectAppData);
    };

    const switchManualAdd = () => {
        setAppStoreData([]);
        updateAppData([]);
        setSelectedAppIds([]);
        setAddAppsIncludeOTA(false);

        form.resetFields();

        setShowManualAddApp(!showManualAddApp);
    };

    const manualAddAppText = showManualAddApp ? 'Search for my app' : `Enter my app's information manually`;
    const titleText = orgHasApps ? 'Add a new app!' : 'Welcome! Add an app to get started.';
    const subText =
        showManualAddApp ?
            "What's your app's name and platform?"
        :   'Find your App Store or Play Store app. You can select multiple apps.';
    const showPlaneIllustration = showManualAddApp ? true : appData.length === 0;

    const searchAppStoreAPICall = React.useCallback(
        (controller: AbortController, queryString: string, countryCode: string) => {
            API.appStoreSearch({ queryString: queryString, countryCode: countryCode }, auth, controller.signal)
                .then((response) => {
                    const flattenedResponse: AppStoreMinimalApp[] = flatten(response);

                    setAppStoreData(flattenedResponse);
                    updateAppData(flattenedResponse);
                })
                .catch((e) => {
                    if (e.name === 'AbortError') {
                        return;
                    } else {
                        globalMessage.error(e.message);
                    }
                })
                .finally(() => setSearchIsLoading(false));
        },
        [auth],
    );

    const searchAppStore = (countryCode: string, queryString?: string) => {
        if (!queryString || queryString === '') {
            setAppStoreData([]);
            updateAppData([]);
            setSelectedAppIds([]);

            return;
        }

        // cancel previous request if there was one
        controller.abort();

        controller = new AbortController();

        setSearchIsLoading(true);

        searchAppStoreAPICall(controller, queryString, countryCode);
    };

    const changeHandler = (countryCode: string, query?: string) => {
        searchAppStore(countryCode, query);
    };

    const debouncedChangeHandler = React.useCallback(debounce(changeHandler, 300), []);

    const onChange = (e) => {
        const value = e.target.value;

        debouncedChangeHandler(selectedCountryCode, value);
    };

    const countryPicker = (
        <Menu
            onClick={(event) => {
                const countryCode = event.key as string;
                setSelectedCountryCode(countryCode);

                const selectedCountry = allSortedCountries.find((country) => country.code === countryCode);

                setSelectedCountryName(selectedCountry.country.name);
            }}
            selectedKeys={[selectedCountryCode]}
            style={{ overflow: 'scroll', maxHeight: 300 }}
        >
            {allSortedCountries.map((country) => {
                return <Menu.Item key={country.code}>{country.country.name}</Menu.Item>;
            })}
        </Menu>
    );

    const addSelectedApps = () => {
        const selectedApps = appStoreData.filter((app) => {
            const platformedAppId = platformDifferentiatedAppId(app.appId, app.appPlatform);

            return selectedAppIds.includes(platformedAppId);
        });
        const selectedAppsRequestData: AddAppData[] = selectedApps.map((selectedApp) => {
            return {
                name: selectedApp.appName,
                platform: selectedApp.appPlatform,
                appStoreId: selectedApp.appId,
                id: selectedApp.id,
                imageUrl: selectedApp.appIconUrl ?? '',
                includeOTAPlatform: addAppsIncludeOTA,
                countryCode: selectedCountryCode,
            };
        });

        const addAppsRequest: AddAppsRequest = {
            orgId: props.orgId,
            orgIdParam,
            appsToAdd: selectedAppsRequestData,
        };

        dispatch(tryAddApps(addAppsRequest));
    };

    const addApp = () => {
        if (showManualAddApp) {
            form.submit();
        } else {
            addSelectedApps();
        }
    };

    const manualAddAppSubmit = (values) => {
        const createAppRequest: CreateAppRequest = {
            orgId: props.orgId,
            orgIdParam,
            name: values.appName,
            platform: values.appPlatform,
            appId: values.appId ?? null,
            includeOTAPlatform: values.needsOta,
            countryCode: values.primaryCountry ?? null,
        };

        dispatch(tryCreateApp(createAppRequest));
    };

    React.useEffect(() => {
        return () => {
            if (!props.open || showManualAddApp) {
                debouncedChangeHandler.cancel();
            }
        };
    }, [props.open, showManualAddApp]);

    React.useEffect(() => {
        if (createAppSuccess || addAppsSuccess) {
            props.onCancel();
        }

        if (createAppSuccess) {
            globalMessage.success('App successfully added!');
        } else if (addAppsSuccess) {
            globalMessage.success('Apps successfully added!');
        }
    }, [createAppSuccess, addAppsSuccess]);

    React.useEffect(() => {
        if (createAppError) {
            globalMessage.error(createAppError);
        } else if (addAppsError) {
            globalMessage.error(addAppsError);
        }
    }, [createAppError, addAppsError]);

    const containerProps: React.CSSProperties =
        showManualAddApp ?
            {}
        :   {
                height: 310,
            };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {props.showAsOnboarding && (
                <Button
                    type="link"
                    onClick={props.onCancel}
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        color: antGrey.grey6,
                        textDecorationLine: 'underline',
                        textDecorationStyle: 'solid',
                    }}
                >
                    Skip this step for now
                </Button>
            )}
            <Space direction="vertical" style={{ marginBottom: 24 }}>
                <Title level={3} style={{ marginBottom: 0 }}>
                    {titleText}
                </Title>
                <Text type="secondary">
                    {subText}{' '}
                    {props.showAsOnboarding && (
                        <>
                            (Or, try Runway in{' '}
                            <Button type="link" onClick={props.onShowDemoMode} style={{ padding: 0 }}>
                                demo mode
                            </Button>
                            )
                        </>
                    )}
                </Text>
                {!showManualAddApp && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
                        <Search
                            placeholder="My app's name"
                            prefix={<SearchOutlined style={{ color: antGrey.grey5 }} />}
                            style={{ width: 300 }}
                            loading={searchIsLoading}
                            onChange={onChange}
                            onSearch={(value) => searchAppStore(selectedCountryCode, value)}
                            allowClear
                            autoFocus
                        />
                        <Dropdown overlay={countryPicker} trigger={['click']} placement="bottomRight">
                            <Text type="secondary">
                                Searching {selectedCountryName} stores (
                                <Button
                                    type="link"
                                    style={{ padding: 0, margin: 0 }}
                                    onClick={(e) => e.preventDefault()}
                                >
                                    change
                                </Button>
                                )
                            </Text>
                        </Dropdown>
                    </div>
                )}
            </Space>
            <div style={{ display: 'flex', flexDirection: 'column', ...containerProps }}>
                {!showManualAddApp && appData.length > 0 && (
                    <div style={{ height: 240, overflow: 'hidden', display: 'flex' }}>
                        <div
                            style={{
                                display: 'flex',
                                overflowY: 'scroll',
                                overflowX: 'hidden',
                                flexBasis: 300,
                                minHeight: 0,
                                minWidth: 0,
                                flexGrow: 1,
                                flexShrink: 1,
                            }}
                        >
                            <Row gutter={[16, 16]}>
                                {appData.map((app, index) => {
                                    const platformedAppId = platformDifferentiatedAppId(app.appId, app.appPlatform);

                                    return (
                                        <Col span={8} key={index}>
                                            <SelectAppComponent
                                                {...app}
                                                key={index}
                                                isSelected={selectedAppIds.includes(platformedAppId)}
                                                onSelectApp={(isSelected, platformedAppId) => {
                                                    if (isSelected) {
                                                        const newSelectedAppIds = [...selectedAppIds, platformedAppId];

                                                        setSelectedAppIds(uniq(newSelectedAppIds));
                                                        updateAppData(appStoreData);
                                                    } else {
                                                        const newSelectedAppIds = selectedAppIds;
                                                        remove(
                                                            newSelectedAppIds,
                                                            (itemId) => itemId === platformedAppId,
                                                        );

                                                        setSelectedAppIds(newSelectedAppIds);
                                                        updateAppData(appStoreData);
                                                    }
                                                }}
                                            />
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    </div>
                )}
                {!showManualAddApp && selectedAppIds.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
                        <Checkbox onChange={(e) => setAddAppsIncludeOTA(e.target.checked)}>
                            <Text type="secondary">
                                {selectedAppIds.length > 1 ?
                                    `Updates to these apps are also delivered via an OTA framework (e.g. CodePush,
                                            Expo)`
                                :   `Updates to this app are also delivered via an OTA framework (e.g. CodePush,
                                            Expo)`
                                }
                            </Text>
                        </Checkbox>
                    </div>
                )}
                {showManualAddApp && (
                    <Form
                        form={form}
                        initialValues={{ appName: null, appPlatform: AppPlatform.Ios, needsOta: false }}
                        onFinish={manualAddAppSubmit}
                        preserve={false}
                        layout="vertical"
                    >
                        <Form.Item name="appName" rules={[{ required: true, message: 'App name is required' }]}>
                            <Input
                                style={{ width: 300 }}
                                placeholder="Enter your app name (required)"
                                autoFocus
                                autoComplete="off"
                            ></Input>
                        </Form.Item>
                        <Form.Item name="appId">
                            <Input
                                style={{ width: 300 }}
                                placeholder="Enter your bundle ID or package name"
                                autoComplete="off"
                            ></Input>
                        </Form.Item>
                        <Form.Item name="primaryCountry" help="Your app's primary country" style={{ width: 300 }}>
                            <Select
                                placeholder="Select country"
                                showSearch
                                optionFilterProp="children"
                                filterOption={filterOptionLabel}
                            >
                                {allSortedCountries.map((country, index) => {
                                    return (
                                        <Select.Option
                                            key={`${country.code}-${index}`}
                                            value={country.code}
                                            label={country.country.name}
                                        >
                                            {country.country.name}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        </Form.Item>
                        <Form.Item name="appPlatform" rules={[{ required: true, message: 'App platform is required' }]}>
                            <Radio.Group>
                                <Radio value={AppPlatform.Ios}>
                                    <Space size={4}>
                                        {iconForPlatform(AppPlatform.Ios, antGrey.grey6)}
                                        <Text type="secondary">iOS</Text>
                                    </Space>
                                </Radio>
                                <Radio value={AppPlatform.Android}>
                                    <Space size={4}>
                                        {iconForPlatform(AppPlatform.Android, runwayColors.androidGreen)}
                                        <Text type="secondary">Android</Text>
                                    </Space>
                                </Radio>
                            </Radio.Group>
                        </Form.Item>
                        <Form.Item name="needsOta" valuePropName="checked">
                            <Checkbox>
                                <Text type="secondary">
                                    Updates to this app are also delivered via an OTA framework (e.g. CodePush, Expo)
                                </Text>
                            </Checkbox>
                        </Form.Item>
                    </Form>
                )}
                {showPlaneIllustration && (
                    <div
                        style={{
                            flexGrow: 1,
                            display: 'flex',
                            alignItems: 'center',
                            marginTop: 24,
                            marginBottom: 24,
                        }}
                    >
                        <img src={planeIllustrationUrl} width={50} height={50} />
                    </div>
                )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
                <Button
                    type="link"
                    onClick={() => switchManualAdd()}
                    style={{ textDecorationLine: 'underline', textDecorationStyle: 'solid', padding: 0 }}
                >
                    {manualAddAppText}
                </Button>
                <PermissionedActionButton
                    actionType={RunwayUserAction.UACreateDeleteApp}
                    icon={<PlusOutlined />}
                    onClick={addApp}
                    type="primary"
                    loading={showManualAddApp ? createAppIsLoading : addAppsIsLoading}
                    htmlType="submit"
                    disabled={addButtonDisabled}
                >
                    Add
                </PermissionedActionButton>
            </div>
        </div>
    );
};
